/**
 * The seam between the session-recording UI and the background upload queue.
 *
 * After the provider taps Finish, the UI enqueues the recording; the queue
 * (registered at app start) joins the local segments, runs the resumable S3
 * multipart upload (`upload` → PUT parts → `complete`), survives app restarts,
 * honours the per-device "Upload on Wi-Fi only" setting, and deletes the local
 * audio once the server confirms the upload. The UI renders
 * {@link SessionRecordingUploadState} for the "Saved on this phone → Uploading"
 * steps; server-side steps (transcribing, drafting) come from the API.
 */

export type SessionRecordingUploadPhase =
  | 'queued'
  | 'waiting_for_wifi'
  | 'waiting_for_network'
  | 'preparing'
  | 'uploading'
  | 'completing'
  | 'done'
  | 'failed'

export interface SessionRecordingUploadState {
  recordingId: string
  sessionUuid: string
  phase: SessionRecordingUploadPhase
  /** 0..1 across all parts. */
  progress: number
  bytesTotal: number | null
  /** User-safe message for `failed` / retrying states. */
  error: string | null
  attempt: number
  /** Epoch ms of the next automatic retry, when backing off. */
  nextRetryAt: number | null
}

export interface SessionRecordingUploader {
  readonly kind: 'native' | 'unavailable'
  enqueue(input: { recordingId: string; sessionUuid: string; durationSec: number }): Promise<void>
  /** Skip the backoff and try now (e.g. "Retry" button). */
  retryNow(recordingId: string): void
  /** Forget a queued upload (discard / withdraw) — local audio is removed by the recorder. */
  cancel(recordingId: string): Promise<void>
  getState(recordingId: string): SessionRecordingUploadState | null
  list(): SessionRecordingUploadState[]
  subscribe(listener: () => void): () => void
  /** Per-device "Upload on Wi-Fi only" (default off). */
  getWifiOnly(): boolean
  /** The persisted Wi-Fi-only setting, once it has been read from device storage. */
  loadWifiOnly(): Promise<boolean>
  setWifiOnly(on: boolean): Promise<void>
}

export const unavailableSessionRecordingUploader: SessionRecordingUploader = {
  kind: 'unavailable',
  enqueue: async () => {
    throw new Error('Uploads need the Ascend app on your phone or tablet.')
  },
  retryNow: () => undefined,
  cancel: async () => undefined,
  getState: () => null,
  list: () => [],
  subscribe: () => () => undefined,
  getWifiOnly: () => false,
  loadWifiOnly: async () => false,
  setWifiOnly: async () => undefined,
}
